import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import FaIconUvilo from '@erikdakoda/ui/components/FaIconUvilo';
import HoverListItem from '@erikdakoda/ui/components/HoverListItem';
import DropdownMenu from '@erikdakoda/ui/components/DropdownMenu';
import type { MenuItemType } from '@erikdakoda/ui/components/MenuItems';
import { useUpdateArtifact } from '@erikdakoda/database/hooks/artifact';
import { useCurrentUser } from '@erikdakoda/ui/hooks/useCurrentUser';
import { useConfirm } from '@erikdakoda/confirm-ui/useConfirm';
import { useSnack } from '@erikdakoda/ui/hooks/useSnack';
import { useException } from '@erikdakoda/ui/hooks/useException';
import type { ArtifactForSidebar } from '@erikdakoda/artifact/types';
import { doFetch } from '@erikdakoda/ui/shared/doFetch';
import { restoreItems } from '@erikdakoda/ui/shared/restoreItems';
import { getArtifactType } from '../shared/artifactRegistry';
import { pushArtifactToPanel } from '../shared/pushArtifactToPanel';

type ArtifactListItemProps = {
  artifact: ArtifactForSidebar;
  onChanged?: () => void;
};

/**
 * Sidebar row for a persisted artifact with type icon, keep/trash/delete actions, and panel open on click.
 */
export default function ArtifactListItem({ artifact, onChanged }: ArtifactListItemProps) {
  'use memo';

  const { t } = useTranslation('artifact-ui');
  const { isAdmin } = useCurrentUser();
  const confirm = useConfirm();
  const { snack } = useSnack();
  const exception = useException();
  const [isLoading, setIsLoading] = useState(false);
  const updateArtifact = useUpdateArtifact().mutateAsync;

  const typeDefinition = getArtifactType(artifact.type);
  const typeIcon = typeDefinition?.icon ?? 'file';

  async function handleKeepChange(keep: boolean): Promise<void> {
    setIsLoading(true);
    const artifactId = artifact.id;
    try {
      await updateArtifact(
        {
          where: { id: artifactId },
          data: { keep },
        },
        { optimisticUpdate: true },
      );
      snack.success(keep ? t('artifactKept') : t('artifactUnkept'));
      onChanged?.();
    } catch (err: unknown) {
      exception(err);
    } finally {
      setIsLoading(false);
    }
  }

  async function handleTrashArtifact(): Promise<void> {
    setIsLoading(true);
    const artifactId = artifact.id;
    try {
      await updateArtifact({
        where: { id: artifactId },
        data: { trashedAt: new Date() },
      });
      const undo = async (): Promise<void> => {
        const { refusedIds = [] } = await restoreItems('Artifact', [artifactId]);
        onChanged?.();
        if (refusedIds.length > 0) {
          throw new Error(t('artifactRestoreFailed'));
        }
      };
      snack.success(t('artifactTrashed'), { undo });
      onChanged?.();
    } catch (err: unknown) {
      exception(err);
    } finally {
      setIsLoading(false);
    }
  }

  async function handleDeleteArtifact(): Promise<void> {
    const artifactId = artifact.id;
    const { confirmed } = await confirm({
      title: t('artifactDeleteConfirmTitle'),
      description: t('artifactDeleteConfirmDescription'),
    });
    if (!confirmed) return;

    await doFetch({
      rowId: artifactId,
      url: '/api/delete-items',
      method: 'POST',
      params: { ids: [artifactId], modelName: 'Artifact' },
      setIsLoading,
      snack,
      exception,
    });
    onChanged?.();
  }

  function handleOpen(): void {
    pushArtifactToPanel({
      identifier: artifact.artifactKey,
      type: artifact.type,
      title: artifact.name,
      content: artifact.content,
      display:
        artifact.display === 'inline' || artifact.display === 'panel'
          ? artifact.display
          : undefined,
    });
  }

  const menuItems: MenuItemType[] = [
    {
      title: artifact.keep ? t('artifactUnkeep') : t('artifactKeep'),
      icon: artifact.keep ? 'bookmark-slash' : 'bookmark',
      value: 'keep',
      onClick: () => {
        void handleKeepChange(!artifact.keep);
      },
    },
    {
      title: t('artifactTrash'),
      icon: 'trash-can',
      value: 'trash',
      onClick: () => {
        void handleTrashArtifact();
      },
    },
  ];

  if (isAdmin) {
    menuItems.push({
      title: t('artifactDelete'),
      icon: 'delete-right',
      value: 'delete',
      onClick: () => {
        void handleDeleteArtifact();
      },
    });
  }

  return (
    <HoverListItem
      className="artifact-list-item"
      forceHover={isLoading}
      secondaryAction={
        <DropdownMenu
          className={`artifact-list-item-${artifact.id}-dropdown`}
          icon="ellipsis-stroke-vertical"
          items={menuItems}
          position="left"
          loading={isLoading}
        />
      }
    >
      <ListItemButton
        className="artifact-list-item-button"
        onClick={handleOpen}
        sx={{ paddingY: 2, '& .MuiListItemIcon-root': { minWidth: 'auto' } }}
      >
        <ListItemIcon>
          <FaIconUvilo
            size="lg"
            icon={typeIcon}
            prefix="fal"
            title={typeDefinition?.name ?? artifact.type}
          />
        </ListItemIcon>
        <ListItemText primary={artifact.name} />
      </ListItemButton>
    </HoverListItem>
  );
}
